import { AsyncPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, debounceTime, map, tap } from 'rxjs';

import { CheckoutFacade } from 'ish-core/facades/checkout.facade';
import { ProductContextFacade } from 'ish-core/facades/product-context.facade';
import { ProductQuantityComponent } from 'ish-shared/components/product/product-quantity/product-quantity.component';

@Component({
  selector: 'custom-forgot-something',
  templateUrl: './forgot-something.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ProductContextFacade],
  imports: [ReactiveFormsModule, FormlyModule, ProductQuantityComponent, FaIconComponent, AsyncPipe, TranslateModule],
})
export class ForgotSomethingComponent implements OnInit, AfterViewInit {
  private checkoutFacade = inject(CheckoutFacade);
  private translate = inject(TranslateService);
  private context = inject(ProductContextFacade);

  directOrderForm = new UntypedFormGroup({});
  fields: FormlyFieldConfig[];
  model = { sku: '' };
  hasQuantityError$: Observable<boolean>;
  loading$: Observable<boolean>;

  ngOnInit() {
    this.fields = this.getFields();
    this.hasQuantityError$ = this.context.select('hasQuantityError');
    this.context.set('quantity', () => 1);
    this.context.config = { quantity: true };
  }

  /**
   * Set the form control field to the product context and handle its behavior.
   */
  ngAfterViewInit() {
    this.context.connect(
      'sku',
      this.directOrderForm.get('sku').valueChanges.pipe(
        tap(value => {
          if (value !== this.directOrderForm.value.sku) {
            this.context.set('loading', () => true);
          }
        }),
        debounceTime(500)
      )
    );
    const skuControl = this.directOrderForm.get('sku');
    skuControl.setAsyncValidators(() =>
      this.context.select('product').pipe(
        map(product => {
          this.context.set('quantity', () => (this.context.get('quantity') ? this.context.get('quantity') : 1));
          const sku = skuControl.value?.trim?.();
          if (!sku) {
            return null;
          }
          return product?.failed ? { validProduct: true } : null;
        })
      )
    );

    this.context.connect('maxQuantity', this.checkoutFacade.basketMaxItemQuantity$);
    this.loading$ = this.context.select('loading');
  }

  onSubmit() {
    this.context.addToBasket();
    this.directOrderForm.reset();
    this.context.set('quantity', () => 1);
  }

  private getFields(): FormlyFieldConfig[] {
    return [
      {
        key: 'sku',
        type: 'search-input-field',
        props: {
          fieldClass: 'col-12',
          placeholder: 'shopping_cart.direct_order.item_placeholder',
          attributes: { autocomplete: 'off' },
        },
        modelOptions: {
          updateOn: 'change',
          debounce: { default: 1 },
        },
        validation: {
          messages: {
            validProduct: () => this.translate.get('quickorder.page.error.invalid.product', { 0: this.model.sku }),
          },
        },
      },
    ];
  }
}
